import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, BarChart, Bar, Cell
} from "recharts";
import {
    Radio, Activity, ShieldAlert, Zap, Globe, MessageSquare,
    ChevronRight, AlertTriangle, Cpu, TrendingUp, Hash,
    Terminal, Clock, Share2, Info, Disc, Lock, ExternalLink,
    RefreshCw, Fingerprint, Shield, Filter, Search, X,
    ChevronDown, CheckCircle2, ArrowUpRight, AlertOctagon,
    Layers, Play, ShieldCheck, Newspaper, Server, Eye
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { backendURL } from "../config";

// --- Color Palette for Platform Distribution ---
const PLATFORM_COLORS = {
    Telegram: "#38bdf8",
    Discord: "#818cf8",
    BreachForums: "#f43f5e",
    "XSS.is": "#f59e0b",
    "Onion/LeakSite": "#10b981",
    Pastebin: "#a855f7",
    Default: "#64748b"
};

const SEVERITY_CONFIG = {
    CRITICAL: {
        bg: "bg-red-500/10 border-red-500/30 text-red-400",
        badge: "bg-red-500 text-white",
        dot: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]"
    },
    HIGH: {
        bg: "bg-orange-500/10 border-orange-500/30 text-orange-400",
        badge: "bg-orange-500 text-white",
        dot: "bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.8)]"
    },
    MEDIUM: {
        bg: "bg-amber-500/10 border-amber-500/30 text-amber-400",
        badge: "bg-amber-500 text-white",
        dot: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)]"
    },
    LOW: {
        bg: "bg-blue-500/10 border-blue-500/30 text-blue-400",
        badge: "bg-blue-500 text-white",
        dot: "bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]"
    }
};

// Platform Icon Resolver
const PlatformIcon = ({ platform = "", className = "w-4 h-4" }) => {
    const p = platform.toLowerCase();
    if (p.includes("telegram")) return <MessageSquare className={`${className} text-sky-400`} />;
    if (p.includes("discord")) return <Disc className={`${className} text-indigo-400`} />;
    if (p.includes("onion") || p.includes("darknet")) return <Globe className={`${className} text-emerald-400`} />;
    if (p.includes("breach")) return <AlertOctagon className={`${className} text-rose-400`} />;
    if (p.includes("xss") || p.includes("forum")) return <Terminal className={`${className} text-amber-400`} />;
    if (p.includes("pastebin")) return <Hash className={`${className} text-purple-400`} />;
    return <Radio className={`${className} text-blue-400`} />;
};

// Custom Tooltip for Threat Persistence Chart
const CustomChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
        <div className="bg-[#0F172A]/95 border border-white/10 p-3 rounded-xl shadow-2xl backdrop-blur-md text-xs font-mono min-w-[190px]">
            <div className="flex items-center justify-between pb-1.5 border-b border-white/10 mb-2">
                <span className="text-slate-400 font-bold flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-blue-400" /> {label}
                </span>
                <span className="text-[10px] text-slate-500 uppercase">Bucket Window</span>
            </div>
            <div className="space-y-1.5">
                {payload.map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 text-[11px]">
                        <span className="flex items-center gap-1.5 capitalize font-medium" style={{ color: entry.color }}>
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                            {entry.name}:
                        </span>
                        <span className="font-bold text-slate-200">
                            {entry.value !== undefined ? (typeof entry.value === 'number' ? entry.value : entry.value) : 0}
                            {entry.name.toLowerCase().includes("confidence") || entry.name.toLowerCase().includes("intensity") ? "%" : ""}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Top Stat Block
const StatBlock = ({ title, value, sub, icon: Icon, color = "blue", badge = null, trend = null }) => {
    const colorClasses = {
        blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", glow: "group-hover:bg-blue-500/10" },
        orange: { bg: "bg-orange-500/10", border: "border-orange-500/20", text: "text-orange-400", glow: "group-hover:bg-orange-500/10" },
        red: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", glow: "group-hover:bg-red-500/10" },
        purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400", glow: "group-hover:bg-purple-500/10" },
        emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", glow: "group-hover:bg-emerald-500/10" },
        cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400", glow: "group-hover:bg-cyan-500/10" }
    };
    const c = colorClasses[color] || colorClasses.blue;

    return (
        <div className="bg-[#0F172A]/90 backdrop-blur-xl border border-white/[0.08] p-5 rounded-2xl relative overflow-hidden group shadow-xl transition-all duration-300 hover:border-white/[0.15]">
            <div className={`absolute top-0 right-0 w-28 h-28 ${c.bg} blur-3xl -mr-12 -mt-12 transition-all duration-500 ${c.glow}`} />
            
            <div className="flex items-center justify-between mb-3 relative z-10">
                <div className={`p-2.5 rounded-xl ${c.bg} border ${c.border} ${c.text}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-1.5">
                    {badge && (
                        <span className="text-[9px] font-black font-mono px-2 py-0.5 rounded-md bg-[#1E293B] border border-white/[0.08] text-slate-300 uppercase tracking-wider">
                            {badge}
                        </span>
                    )}
                </div>
            </div>

            <div className="relative z-10">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{title}</p>
                <div className="text-2xl lg:text-3xl font-black text-slate-100 font-mono tracking-tight leading-none mb-1.5">
                    {value}
                </div>
                <p className="text-[11px] text-slate-400 font-medium truncate">{sub}</p>
            </div>
        </div>
    );
};

// ─── Main Early Warning Component ──────────────────────────────────
export default function EarlyWarning() {
    // Data states
    const [rumors, setRumors] = useState([]);
    const [totalRumors, setTotalRumors] = useState(0);
    const [stats, setStats] = useState({ stats: [], distribution: [], categories: [], severityCounts: {} });
    const [analysis, setAnalysis] = useState({
        summary: "Monitoring baseline pre-breach signal persistence...",
        prediction: "48-72 Hours (Emerging Chatter)",
        signalStrength: 42,
        threatLevel: "ELEVATED",
        totalSignals: 0,
        activeCount24h: 0,
        criticalCount: 0,
        highCount: 0,
        monitoredPlatforms: 0,
        monitoredAssets: 0,
        recommendations: []
    });

    // OSINT Articles state
    const [osintItems, setOsintItems] = useState([]);
    const [osintTotal, setOsintTotal] = useState(0);
    const [osintPage, setOsintPage] = useState(1);
    const [osintLoading, setOsintLoading] = useState(false);

    // UI & Filter states
    const [activeTab, setActiveTab] = useState("signals"); // 'signals' | 'osint'
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [timeRange, setTimeRange] = useState("24h");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedPlatform, setSelectedPlatform] = useState("all");
    const [selectedSeverity, setSelectedSeverity] = useState("all");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [expandedRumorId, setExpandedRumorId] = useState(null);
    const [chartMode, setChartMode] = useState("area"); // 'area' | 'bar'

    const navigate = useNavigate();

    // Mitigation Action Modal state
    const [mitigationModal, setMitigationModal] = useState({
        isOpen: false,
        actionTitle: "",
        targetedAsset: "",
        signalId: null,
        notes: "",
        submitting: false,
        successMsg: "",
        createdCaseId: null,
        createdIncidentId: null
    });

    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Fetch Pre-Breach Signals & Analysis
    const fetchEarlyWarningData = useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        setRefreshing(true);

        const params = new URLSearchParams();
        if (timeRange && timeRange !== "all") params.append("timeRange", timeRange);
        if (selectedPlatform && selectedPlatform !== "all") params.append("platform", selectedPlatform);
        if (selectedSeverity && selectedSeverity !== "all") params.append("severity", selectedSeverity);
        if (selectedCategory && selectedCategory !== "all") params.append("threatCategory", selectedCategory);
        if (searchQuery.trim()) params.append("search", searchQuery.trim());
        params.append("limit", "50");

        const qs = params.toString() ? `?${params.toString()}` : "";

        try {
            const [rumorsRes, statsRes, analysisRes] = await Promise.all([
                fetch(`${backendURL}/api/threat/rumors${qs}`, { headers }),
                fetch(`${backendURL}/api/threat/stats${qs}`, { headers }),
                fetch(`${backendURL}/api/threat/analysis`, { headers })
            ]);

            const safeJson = async (r) => {
                try { return r.ok ? await r.json() : null; }
                catch { return null; }
            };

            const [rData, sData, aData] = await Promise.all([
                safeJson(rumorsRes),
                safeJson(statsRes),
                safeJson(analysisRes)
            ]);

            if (rData) {
                setRumors(rData.rumors || []);
                setTotalRumors(rData.total || 0);
            }
            if (sData) {
                setStats({
                    stats: sData.stats || [],
                    distribution: sData.distribution || [],
                    categories: sData.categories || [],
                    severityCounts: sData.severityCounts || {}
                });
            }
            if (aData) {
                setAnalysis(aData);
            }
        } catch (err) {
            console.error("Early Warning data fetch error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [timeRange, selectedPlatform, selectedSeverity, selectedCategory, searchQuery]);

    // Fetch OSINT Feed Articles (Real Mongo darkweb_headlines)
    const fetchOsintFeed = useCallback(async (page = 1) => {
        setOsintLoading(true);
        try {
            const p = new URLSearchParams();
            p.append("page", String(page));
            p.append("limit", "15");
            if (searchQuery.trim()) p.append("search", searchQuery.trim());

            const res = await fetch(`${backendURL}/api/threat/osint-feed?${p.toString()}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setOsintItems(data.items || []);
                setOsintTotal(data.total || 0);
                setOsintPage(data.page || 1);
            }
        } catch (err) {
            console.error("OSINT feed fetch error:", err);
        } finally {
            setOsintLoading(false);
        }
    }, [searchQuery]);

    useEffect(() => {
        fetchEarlyWarningData();
    }, [fetchEarlyWarningData]);

    useEffect(() => {
        if (activeTab === "osint") {
            fetchOsintFeed(osintPage);
        }
    }, [activeTab, osintPage, fetchOsintFeed]);

    // Handle Auto-Refresh timer
    useEffect(() => {
        let interval = null;
        if (autoRefresh) {
            interval = setInterval(() => {
                fetchEarlyWarningData(true);
                if (activeTab === "osint") fetchOsintFeed(osintPage);
            }, 30000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [autoRefresh, fetchEarlyWarningData, activeTab, fetchOsintFeed, osintPage]);

    // Execute Tactical Mitigation API Call
    const handleExecuteMitigation = async () => {
        setMitigationModal(prev => ({ ...prev, submitting: true }));
        try {
            const res = await fetch(`${backendURL}/api/threat/mitigate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...headers
                },
                body: JSON.stringify({
                    actionTitle: mitigationModal.actionTitle,
                    targetedAsset: mitigationModal.targetedAsset,
                    signalId: mitigationModal.signalId,
                    notes: mitigationModal.notes
                })
            });

            if (res.ok) {
                const data = await res.json();
                setMitigationModal(prev => ({
                    ...prev,
                    submitting: false,
                    successMsg: data.message || "Mitigation action deployed successfully!",
                    createdCaseId: data.caseId,
                    createdIncidentId: data.incidentId
                }));
                fetchEarlyWarningData(true);
            } else {
                setMitigationModal(prev => ({ ...prev, submitting: false }));
            }
        } catch (err) {
            console.error("Mitigation deploy error:", err);
            setMitigationModal(prev => ({ ...prev, submitting: false }));
        }
    };

    // Open mitigation modal helper
    const openMitigateDialog = (actionTitle, targetedAsset, signalId = null) => {
        setMitigationModal({
            isOpen: true,
            actionTitle,
            targetedAsset: targetedAsset || "Edge Infrastructure",
            signalId,
            notes: "",
            submitting: false,
            successMsg: "",
            createdCaseId: null,
            createdIncidentId: null
        });
    };

    // Filter count helper
    const activeFilterCount = (selectedPlatform !== "all" ? 1 : 0) +
        (selectedSeverity !== "all" ? 1 : 0) +
        (selectedCategory !== "all" ? 1 : 0) +
        (timeRange !== "24h" ? 1 : 0) +
        (searchQuery.trim() ? 1 : 0);

    const clearAllFilters = () => {
        setSelectedPlatform("all");
        setSelectedSeverity("all");
        setSelectedCategory("all");
        setTimeRange("24h");
        setSearchQuery("");
    };

    return (
        <div className="p-4 lg:p-8 space-y-6 max-w-[1700px] mx-auto min-h-screen text-slate-100 font-sans pb-24">

            {/* ── HEADER / OPERATIONS CONTROL BAR ── */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5 bg-[#0F172A]/90 backdrop-blur-xl p-6 rounded-3xl border border-white/[0.08] shadow-2xl relative overflow-hidden">
                <div className="flex items-start sm:items-center gap-4 relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600/20 to-indigo-600/10 border border-blue-500/30 flex items-center justify-center shrink-0 shadow-[0_0_25px_rgba(59,130,246,0.2)]">
                        <Radio className="w-7 h-7 text-blue-400 animate-pulse" />
                    </div>
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl lg:text-3xl font-black text-slate-100 tracking-tight uppercase">
                                Threat Early Warning
                            </h1>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-500/20 text-red-400 border border-red-500/30 shadow-[0_0_12px_rgba(239,68,68,0.3)]">
                                Pre-Breach Intel
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                OSINT Engine Live
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-2 font-medium">
                            <Activity className="w-3.5 h-3.5 text-blue-400" />
                            Tactical Signal Intelligence &bull; Continuous Dark Web &amp; Breach Forum Telemetry &bull; Real MongoDB Engine
                        </p>
                    </div>
                </div>

                {/* Header Action Controls */}
                <div className="flex flex-wrap items-center gap-3 relative z-10">
                    {/* Auto-Refresh Toggle */}
                    <div className="flex items-center gap-2 bg-[#1E293B]/70 px-3 py-1.5 rounded-xl border border-white/[0.06]">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Auto-Sync</span>
                        <button
                            onClick={() => setAutoRefresh(!autoRefresh)}
                            className={`w-9 h-5 rounded-full transition-colors relative flex items-center p-0.5 ${autoRefresh ? "bg-blue-600" : "bg-slate-700"}`}
                        >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoRefresh ? "translate-x-4" : "translate-x-0"}`} />
                        </button>
                    </div>

                    {/* Manual Refresh Button */}
                    <button
                        onClick={() => { fetchEarlyWarningData(); if (activeTab === "osint") fetchOsintFeed(osintPage); }}
                        disabled={refreshing}
                        className="px-4 py-2 bg-[#1E293B]/80 hover:bg-[#1E293B] border border-white/[0.08] hover:border-white/[0.15] text-slate-200 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-blue-400 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Syncing..." : "Refresh"}
                    </button>
                </div>
            </div>

            {/* ── INTELLIGENCE SUMMARY & THREAT PULSE BANNER ── */}
            <div className={`p-5 rounded-3xl border transition-all duration-500 shadow-xl ${
                analysis.threatLevel === "CRITICAL"
                    ? "bg-gradient-to-r from-red-950/40 via-[#0F172A] to-red-950/20 border-red-500/30"
                    : analysis.threatLevel === "HIGH"
                    ? "bg-gradient-to-r from-orange-950/40 via-[#0F172A] to-orange-950/20 border-orange-500/30"
                    : "bg-[#0F172A]/90 border-white/[0.08]"
            }`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 border ${
                            analysis.threatLevel === "CRITICAL" ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-blue-500/10 border-blue-500/20 text-blue-400"
                        }`}>
                            <AlertTriangle className="w-5 h-5 animate-pulse" />
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2.5">
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full border ${
                                    SEVERITY_CONFIG[analysis.threatLevel]?.bg || SEVERITY_CONFIG.HIGH.bg
                                }`}>
                                    {analysis.threatLevel} Threat Persistence
                                </span>
                                <span className="text-[11px] text-slate-400 font-mono">
                                    Forecast Horizon: <strong className="text-slate-200">{analysis.prediction}</strong>
                                </span>
                                <span className="text-[11px] text-slate-400 font-mono">
                                    Active 24H Volume: <strong className="text-blue-400">{analysis.activeCount24h || rumors.length} signals</strong>
                                </span>
                            </div>
                            <p className="text-sm lg:text-base font-bold text-slate-100 leading-snug">
                                {analysis.summary}
                            </p>
                        </div>
                    </div>

                    {/* Quick Tactical Trigger */}
                    {analysis.recommendations && analysis.recommendations.length > 0 && (
                        <div className="shrink-0 flex items-center gap-3 bg-[#1E293B]/70 border border-white/[0.06] p-3 rounded-2xl">
                            <div className="text-left pr-2">
                                <p className="text-[9px] font-black uppercase tracking-widest text-blue-400">Primary SOC Action</p>
                                <p className="text-xs font-bold text-slate-200 max-w-[200px] truncate">{analysis.recommendations[0].title}</p>
                            </div>
                            <button
                                onClick={() => openMitigateDialog(analysis.recommendations[0].title, analysis.recommendations[0].targetedAsset)}
                                className="px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all shadow-lg shadow-blue-600/30 active:scale-95 shrink-0"
                            >
                                <Play className="w-3 h-3" /> Mitigate
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── 5 EXECUTIVE KPI METRIC TILES ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <StatBlock
                    title="Signal Threat Index"
                    value={`${analysis.signalStrength}%`}
                    sub={`Trajectory: ${analysis.threatLevel} Status`}
                    icon={Zap}
                    color={analysis.signalStrength > 75 ? "red" : analysis.signalStrength > 50 ? "orange" : "blue"}
                    badge={analysis.threatLevel}
                />
                <StatBlock
                    title="Pre-Breach Signals"
                    value={analysis.totalSignals || rumors.length}
                    sub={`${analysis.criticalCount || 0} Critical · ${analysis.highCount || 0} High Priority`}
                    icon={ShieldAlert}
                    color="purple"
                    badge="DATABASE"
                />
                <StatBlock
                    title="Impact Window"
                    value={analysis.prediction?.includes(":") ? analysis.prediction.split(":")[0] : analysis.prediction?.split("(")[0] || "48-72h"}
                    sub={analysis.prediction?.includes("(") ? `(${analysis.prediction.split("(")[1]}` : "Estimated Horizon"}
                    icon={Clock}
                    color="orange"
                    badge="AI FORECAST"
                />
                <StatBlock
                    title="Monitored OSINT Sources"
                    value={`${analysis.monitoredPlatforms || 0} Platforms`}
                    sub="Telegram, Dark Forums, Tor, Pastebin"
                    icon={Globe}
                    color="cyan"
                    badge="ACTIVE"
                />
                <StatBlock
                    title="Targeted Surface Exposure"
                    value={`${analysis.monitoredAssets || 0} Vectors`}
                    sub="SSL-VPN, Cloud, AD, DNS, API"
                    icon={Server}
                    color="emerald"
                    badge="INTEGRITY"
                />
            </div>

            {/* ── ADVANCED TELEMETRY & OSINT FILTER TOOLBAR ── */}
            <div className="bg-[#0F172A]/90 backdrop-blur-xl p-4 rounded-2xl border border-white/[0.08] shadow-xl flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                    {/* Filter Icon and Badge */}
                    <div className="flex items-center gap-2 text-slate-400 pr-2 border-r border-slate-800">
                        <Filter className="w-4 h-4 text-blue-400" />
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-200">Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                                {activeFilterCount}
                            </span>
                        )}
                    </div>

                    {/* Search Input */}
                    <div className="flex items-center gap-2 bg-[#1E293B]/60 border border-white/[0.06] rounded-xl px-3 py-1.5 focus-within:border-blue-500/50">
                        <Search className="w-3.5 h-3.5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search chatter, CVE, IOC, asset..."
                            className="bg-transparent text-xs font-medium text-slate-200 outline-none w-56 placeholder:text-slate-500"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchEarlyWarningData()}
                        />
                        {searchQuery && (
                            <button onClick={() => setSearchQuery("")} className="text-slate-500 hover:text-slate-300">
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Platform Selector */}
                    <div className="relative">
                        <select
                            value={selectedPlatform}
                            onChange={(e) => setSelectedPlatform(e.target.value)}
                            className="bg-[#1E293B]/60 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-2 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8"
                        >
                            <option value="all">All Intelligence Platforms</option>
                            <option value="Telegram">Telegram Channels</option>
                            <option value="BreachForums">BreachForums</option>
                            <option value="XSS.is">XSS.is Forum</option>
                            <option value="Onion/LeakSite">Tor Onion / Leak Sites</option>
                            <option value="Discord">Discord Communities</option>
                            <option value="Pastebin">Pastebin Dumps</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Severity Selector */}
                    <div className="relative">
                        <select
                            value={selectedSeverity}
                            onChange={(e) => setSelectedSeverity(e.target.value)}
                            className="bg-[#1E293B]/60 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-2 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8"
                        >
                            <option value="all">All Severity Levels</option>
                            <option value="CRITICAL">Critical Severity</option>
                            <option value="HIGH">High Severity</option>
                            <option value="MEDIUM">Medium Severity</option>
                            <option value="LOW">Low Severity</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Threat Category Selector */}
                    <div className="relative">
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="bg-[#1E293B]/60 border border-white/[0.06] text-slate-200 text-xs font-bold px-3 py-2 rounded-xl outline-none focus:border-blue-500/50 cursor-pointer appearance-none pr-8"
                        >
                            <option value="all">All Threat Vectors</option>
                            <option value="VPN Bypass">VPN Bypass</option>
                            <option value="Credential Exposure">Credential Exposure</option>
                            <option value="Zero-Day Exploit">Zero-Day Exploit</option>
                            <option value="Ransomware Recon">Ransomware Recon</option>
                            <option value="Cloud & S3 Leak">Cloud &amp; S3 Leak</option>
                            <option value="DNS Tunneling">DNS Tunneling</option>
                            <option value="Phishing Infrastructure">Phishing Infrastructure</option>
                            <option value="DDoS Weaponization">DDoS Weaponization</option>
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>

                    {/* Time Range Selector */}
                    <div className="flex items-center gap-1 bg-[#1E293B]/60 p-1 rounded-xl border border-white/[0.06] text-[10px] font-bold">
                        {["1h", "6h", "24h", "7d", "30d", "all"].map((tr) => (
                            <button
                                key={tr}
                                onClick={() => setTimeRange(tr)}
                                className={`px-2.5 py-1 rounded-lg uppercase tracking-wider transition-colors ${
                                    timeRange === tr ? "bg-blue-600 text-white font-black" : "text-slate-400 hover:text-slate-200"
                                }`}
                            >
                                {tr}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Filter Actions */}
                <div className="flex items-center gap-2">
                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearAllFilters}
                            className="px-3.5 py-2 text-slate-400 hover:text-slate-200 text-xs font-bold uppercase tracking-wider transition-colors"
                        >
                            Reset
                        </button>
                    )}
                    <button
                        onClick={() => fetchEarlyWarningData()}
                        disabled={loading}
                        className="px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all shadow-[0_0_20px_rgba(59,130,246,0.3)] active:scale-95 disabled:opacity-50"
                    >
                        <Activity className="w-3.5 h-3.5" /> Apply Filter
                    </button>
                </div>
            </div>

            {/* ── MAIN WORKSPACE (12 COLUMNS) ── */}
            {loading && !refreshing ? (
                <div className="flex flex-col items-center justify-center h-96 gap-4 bg-[#0F172A]/40 rounded-3xl border border-white/[0.04]">
                    <div className="relative">
                        <div className="w-14 h-14 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                        <Radio className="w-6 h-6 text-blue-400 absolute inset-0 m-auto animate-pulse" />
                    </div>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest animate-pulse">
                        Querying Pre-Breach Signals &amp; OSINT Intelligence...
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* ── LEFT COLUMN: Persistence Chart + Tabbed Intel Feeds (8 cols) ── */}
                    <div className="lg:col-span-8 space-y-6">

                        {/* ── Threat Signal Persistence & Confidence Stream ── */}
                        <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] p-6 shadow-xl relative overflow-hidden">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                                <div>
                                    <h3 className="text-sm font-black text-slate-100 uppercase tracking-widest flex items-center gap-2.5">
                                        <TrendingUp className="w-4 h-4 text-blue-400" />
                                        Threat Signal Persistence &amp; Confidence Stream
                                    </h3>
                                    <p className="text-[11px] text-slate-400 mt-0.5">
                                        Time-series intelligence chatter persistence and threat confidence overlay
                                    </p>
                                </div>

                                {/* Chart mode controls */}
                                <div className="flex items-center gap-3">
                                    <div className="bg-[#1E293B]/70 rounded-xl p-1 border border-white/[0.06] flex items-center gap-1 text-[10px] font-bold">
                                        <button
                                            onClick={() => setChartMode("area")}
                                            className={`px-2.5 py-1 rounded-lg uppercase tracking-wider transition-colors ${
                                                chartMode === "area" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
                                            }`}
                                        >
                                            Area Flow
                                        </button>
                                        <button
                                            onClick={() => setChartMode("bar")}
                                            className={`px-2.5 py-1 rounded-lg uppercase tracking-wider transition-colors ${
                                                chartMode === "bar" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"
                                            }`}
                                        >
                                            Threat Bars
                                        </button>
                                    </div>

                                    <div className="hidden sm:flex items-center gap-3 text-[10px] font-mono text-slate-400 pl-2 border-l border-slate-800">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded bg-blue-500" /> Intensity
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded bg-emerald-500" /> Confidence
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {stats.stats.length === 0 ? (
                                <div className="h-64 flex flex-col items-center justify-center text-slate-500 text-xs font-mono uppercase tracking-widest border border-dashed border-slate-800 rounded-2xl gap-2">
                                    <Activity className="w-8 h-8 text-slate-700" />
                                    <span>No threat telemetry recorded in selected window</span>
                                </div>
                            ) : (
                                <div className="h-[280px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        {chartMode === "area" ? (
                                            <AreaChart data={stats.stats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="gradThreatIntensity" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                                                    </linearGradient>
                                                    <linearGradient id="gradThreatConfidence" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                                                <XAxis
                                                    dataKey="time"
                                                    tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                    axisLine={{ stroke: "#1E293B" }}
                                                    tickLine={false}
                                                    interval="preserveStartEnd"
                                                />
                                                <YAxis
                                                    domain={[0, 100]}
                                                    tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickFormatter={(v) => `${v}%`}
                                                />
                                                <Tooltip content={<CustomChartTooltip />} />
                                                <Area
                                                    type="monotone"
                                                    dataKey="intensity"
                                                    name="Intensity"
                                                    stroke="#3b82f6"
                                                    fill="url(#gradThreatIntensity)"
                                                    strokeWidth={2.5}
                                                    dot={false}
                                                    activeDot={{ r: 5, fill: "#3b82f6", stroke: "#fff", strokeWidth: 2 }}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="confidence"
                                                    name="Confidence"
                                                    stroke="#10b981"
                                                    fill="url(#gradThreatConfidence)"
                                                    strokeWidth={1.5}
                                                    strokeDasharray="4 4"
                                                    dot={false}
                                                />
                                            </AreaChart>
                                        ) : (
                                            <BarChart data={stats.stats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                                                <XAxis
                                                    dataKey="time"
                                                    tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                    axisLine={{ stroke: "#1E293B" }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    domain={[0, 100]}
                                                    tick={{ fill: "#64748B", fontSize: 10, fontWeight: 600 }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickFormatter={(v) => `${v}%`}
                                                />
                                                <Tooltip content={<CustomChartTooltip />} />
                                                <Bar dataKey="intensity" name="Intensity" fill="#3b82f6" radius={[4, 4, 0, 0]} opacity={0.8} />
                                                <Bar dataKey="confidence" name="Confidence" fill="#10b981" radius={[4, 4, 0, 0]} opacity={0.8} />
                                            </BarChart>
                                        )}
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* Timeline Summary Footer */}
                            {stats.stats.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between text-[11px] text-slate-400 font-mono gap-3">
                                    <div className="flex items-center gap-4">
                                        <span>Resolution: <strong>{timeRange === "1h" ? "5-min" : timeRange === "6h" ? "30-min" : timeRange === "7d" ? "12-hour" : "2-hour"} buckets</strong></span>
                                        <span>Buckets: <strong>{stats.stats.length}</strong></span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span>Peak Intensity: <strong className="text-blue-400">{Math.max(...stats.stats.map(s => s.intensity || 0))}%</strong></span>
                                        <span>Avg Confidence: <strong className="text-emerald-400">{Math.round(stats.stats.reduce((a, b) => a + (b.confidence || 0), 0) / (stats.stats.length || 1))}%</strong></span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── TABBED SIGNAL INTELLIGENCE & OSINT FEED ── */}
                        <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] shadow-xl overflow-hidden">
                            {/* Tab Switcher */}
                            <div className="p-4 sm:p-5 border-b border-white/[0.06] bg-[#1E293B]/40 flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setActiveTab("signals")}
                                        className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
                                            activeTab === "signals"
                                                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                                : "bg-[#1E293B]/60 text-slate-400 hover:text-slate-200 border border-white/[0.04]"
                                        }`}
                                    >
                                        <MessageSquare className="w-3.5 h-3.5" />
                                        Pre-Breach Signals
                                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-black/30 text-white">
                                            {totalRumors}
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => { setActiveTab("osint"); fetchOsintFeed(1); }}
                                        className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all ${
                                            activeTab === "osint"
                                                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                                : "bg-[#1E293B]/60 text-slate-400 hover:text-slate-200 border border-white/[0.04]"
                                        }`}
                                    >
                                        <Newspaper className="w-3.5 h-3.5" />
                                        Live OSINT &amp; Dark Web Feed
                                        <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] bg-black/30 text-white">
                                            {osintTotal || "1,479+"}
                                        </span>
                                    </button>
                                </div>

                                <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                    {activeTab === "signals" ? "Tactical Signal Stream" : "Global Breach RSS Feeds"}
                                </span>
                            </div>

                            {/* ── TAB 1: PRE-BREACH INTELLIGENCE SIGNALS ── */}
                            {activeTab === "signals" && (
                                <div className="divide-y divide-white/[0.04] max-h-[680px] overflow-y-auto">
                                    {rumors.length === 0 ? (
                                        <div className="py-20 text-center text-slate-500 text-xs font-mono uppercase tracking-widest flex flex-col items-center gap-2">
                                            <Radio className="w-8 h-8 text-slate-700 animate-pulse" />
                                            <span>No signals matching current filter criteria</span>
                                        </div>
                                    ) : (
                                        rumors.map((r, idx) => {
                                            const isExpanded = expandedRumorId === (r._id || idx);
                                            const sev = (r.severity || (r.impactScore >= 80 ? "CRITICAL" : r.impactScore >= 65 ? "HIGH" : "MEDIUM")).toUpperCase();
                                            const sevMeta = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.MEDIUM;
                                            const platformColor = PLATFORM_COLORS[r.platform] || PLATFORM_COLORS.Default;

                                            return (
                                                <div
                                                    key={r._id || idx}
                                                    onClick={() => setExpandedRumorId(isExpanded ? null : (r._id || idx))}
                                                    className={`p-5 transition-all duration-200 cursor-pointer group hover:bg-[#1E293B]/40 relative ${
                                                        isExpanded ? "bg-[#1E293B]/50" : ""
                                                    }`}
                                                >
                                                    {/* Left accent bar for critical/high signals */}
                                                    {sev === "CRITICAL" && (
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]" />
                                                    )}
                                                    {sev === "HIGH" && (
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500" />
                                                    )}

                                                    <div className="flex items-start gap-4">
                                                        {/* Platform Icon Badge */}
                                                        <div
                                                            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border group-hover:scale-105 transition-transform shadow-sm"
                                                            style={{
                                                                backgroundColor: `${platformColor}15`,
                                                                borderColor: `${platformColor}35`
                                                            }}
                                                        >
                                                            <PlatformIcon platform={r.platform} className="w-5 h-5" />
                                                        </div>

                                                        {/* Content Body */}
                                                        <div className="flex-1 min-w-0 space-y-2">
                                                            {/* Top Row: Platform name, Category, Targeted Asset, Timestamp */}
                                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span
                                                                        className="text-xs font-black uppercase tracking-wider"
                                                                        style={{ color: platformColor }}
                                                                    >
                                                                        {r.platform}
                                                                    </span>
                                                                    <span className="text-slate-600">&bull;</span>
                                                                    <span className="text-[11px] text-slate-400 font-medium truncate max-w-[200px]">
                                                                        {r.source || "OSINT Feed"}
                                                                    </span>
                                                                    {r.threatCategory && (
                                                                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-[#1E293B] border border-white/[0.08] text-slate-300">
                                                                            {r.threatCategory}
                                                                        </span>
                                                                    )}
                                                                    {r.cve && r.cve !== "N/A" && (
                                                                        <span className="px-2 py-0.5 rounded-md text-[10px] font-black font-mono bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                                                            {r.cve}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400 shrink-0">
                                                                    <Clock className="w-3 h-3 text-slate-500" />
                                                                    {r.timestamp ? new Date(r.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Live"}
                                                                </div>
                                                            </div>

                                                            {/* Main Rumor Text */}
                                                            <p className="text-xs sm:text-sm font-semibold text-slate-200 leading-relaxed group-hover:text-white">
                                                                {r.rumor}
                                                            </p>

                                                            {/* Bottom Metric Tags */}
                                                            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                                                                <div className="flex flex-wrap items-center gap-2.5">
                                                                    {/* Severity Badge */}
                                                                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border flex items-center gap-1.5 ${sevMeta.bg}`}>
                                                                        <span className={`w-1.5 h-1.5 rounded-full ${sevMeta.dot}`} />
                                                                        {sev}
                                                                    </span>

                                                                    {/* Impact Score */}
                                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-[#0B0F19] border border-white/[0.06] text-blue-400">
                                                                        Impact: <strong>{r.impactScore || 50}/100</strong>
                                                                    </span>

                                                                    {/* Confidence */}
                                                                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-[#0B0F19] border border-white/[0.06] text-emerald-400">
                                                                        Confidence: <strong>{r.confidence || 50}%</strong>
                                                                    </span>

                                                                    {/* Targeted Asset */}
                                                                    {r.targetedAsset && (
                                                                        <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                                                            <Server className="w-3 h-3 text-slate-500" /> Target: <strong className="text-slate-300">{r.targetedAsset}</strong>
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openMitigateDialog(
                                                                                r.mitigation ? `Mitigate ${r.threatCategory || r.targetedAsset}` : "Deploy Edge Defense",
                                                                                r.targetedAsset,
                                                                                r._id
                                                                            );
                                                                        }}
                                                                        className="px-3 py-1 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded-lg text-[10px] font-black uppercase tracking-wider border border-blue-500/30 transition-all flex items-center gap-1"
                                                                    >
                                                                        <Shield className="w-3 h-3" /> Mitigate
                                                                    </button>
                                                                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Expanded Intelligence Panel */}
                                                    {isExpanded && (
                                                        <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-3">
                                                            {r.mitigation && (
                                                                <div className="bg-[#0B0F19]/80 p-3.5 rounded-xl border border-white/[0.06] space-y-1">
                                                                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400 flex items-center gap-1.5">
                                                                        <ShieldCheck className="w-3.5 h-3.5" /> Recommended Mitigation Playbook
                                                                    </p>
                                                                    <p className="text-xs text-slate-300 leading-relaxed font-medium">
                                                                        {r.mitigation}
                                                                    </p>
                                                                </div>
                                                            )}

                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-blue-500/5 p-3 rounded-xl border border-blue-500/10">
                                                                <div className="min-w-0 pr-2">
                                                                    <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest block mb-0.5">
                                                                        Intelligence Source Reference &bull; Trace Verified
                                                                    </span>
                                                                    <code className="text-[11px] text-slate-400 font-mono break-all select-all">
                                                                        {r.reference || "Source Trace Encrypted (Internal IOC Stream)"}
                                                                    </code>
                                                                </div>
                                                                {r.reference && r.reference.startsWith("http") && (
                                                                    <a
                                                                        href={r.reference}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="px-3 py-1.5 bg-blue-600/30 hover:bg-blue-600 text-blue-200 hover:text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 self-start sm:self-auto"
                                                                    >
                                                                        <ExternalLink className="w-3.5 h-3.5" /> Open Trace
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            )}

                            {/* ── TAB 2: LIVE OSINT & DARK WEB RSS FEED (1,478 Real Articles) ── */}
                            {activeTab === "osint" && (
                                <div className="divide-y divide-white/[0.04] max-h-[680px] overflow-y-auto">
                                    {osintLoading ? (
                                        <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-500 text-xs font-mono uppercase tracking-widest">
                                            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                            <span>Loading Verified Global Breach Feeds...</span>
                                        </div>
                                    ) : osintItems.length === 0 ? (
                                        <div className="py-20 text-center text-slate-500 text-xs font-mono uppercase tracking-widest">
                                            No OSINT breach articles matching search query
                                        </div>
                                    ) : (
                                        osintItems.map((article, idx) => (
                                            <div
                                                key={article._id || idx}
                                                className="p-5 hover:bg-[#1E293B]/40 transition-colors space-y-2 group"
                                            >
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                                            {article.source || "Global Threat Feed"}
                                                        </span>
                                                        {article.exposure && (
                                                            <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase bg-red-500/10 text-red-400 border border-red-500/20">
                                                                Exposure Confirmed
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                                                        <Clock className="w-3 h-3 text-slate-500" />
                                                        {article.date ? new Date(article.date).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "Recent"}
                                                    </span>
                                                </div>

                                                <a
                                                    href={article.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-sm font-bold text-slate-100 hover:text-blue-400 transition-colors flex items-start gap-2 leading-snug"
                                                >
                                                    <span>{article.title}</span>
                                                    <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-blue-400 shrink-0 mt-0.5 transition-colors" />
                                                </a>

                                                {article.link && (
                                                    <p className="text-[11px] font-mono text-slate-500 truncate select-all">
                                                        {article.link}
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    )}

                                    {/* Pagination Controls */}
                                    {osintTotal > 15 && (
                                        <div className="p-4 bg-[#1E293B]/60 border-t border-white/[0.06] flex items-center justify-between text-xs font-mono text-slate-400">
                                            <span>Showing page <strong>{osintPage}</strong> of <strong>{Math.ceil(osintTotal / 15)}</strong> ({osintTotal} total items)</span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    disabled={osintPage <= 1 || osintLoading}
                                                    onClick={() => setOsintPage(prev => Math.max(1, prev - 1))}
                                                    className="px-3 py-1 bg-[#0B0F19] hover:bg-[#1E293B] rounded-lg border border-white/[0.08] disabled:opacity-40 transition-colors"
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    disabled={osintPage >= Math.ceil(osintTotal / 15) || osintLoading}
                                                    onClick={() => setOsintPage(prev => prev + 1)}
                                                    className="px-3 py-1 bg-[#0B0F19] hover:bg-[#1E293B] rounded-lg border border-white/[0.08] disabled:opacity-40 transition-colors"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── RIGHT COLUMN: Platform Hotspots + Vectors + Tactical Actions (4 cols) ── */}
                    <div className="lg:col-span-4 space-y-6">

                        {/* ── Platform Hotspots & Chatter Distribution ── */}
                        <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] p-6 shadow-xl space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-black text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-emerald-400" /> Platform Hotspots
                                </h3>
                                <span className="text-[10px] font-mono text-slate-400 bg-[#1E293B] px-2 py-0.5 rounded border border-white/[0.06]">
                                    {stats.distribution.length} Sources
                                </span>
                            </div>

                            <div className="space-y-3">
                                {stats.distribution.length === 0 ? (
                                    <p className="text-xs text-slate-500 font-mono text-center py-6">No platform data available</p>
                                ) : (
                                    stats.distribution.map((item, idx) => {
                                        const color = PLATFORM_COLORS[item.name] || PLATFORM_COLORS.Default;
                                        const isSelected = selectedPlatform === item.name;

                                        return (
                                            <div
                                                key={item.name}
                                                onClick={() => setSelectedPlatform(isSelected ? "all" : item.name)}
                                                className={`p-3 rounded-xl border transition-all duration-200 cursor-pointer space-y-1.5 ${
                                                    isSelected
                                                        ? "bg-blue-600/20 border-blue-500 ring-1 ring-blue-500"
                                                        : "bg-[#1E293B]/40 border-white/[0.04] hover:bg-[#1E293B]/80 hover:border-white/[0.1]"
                                                }`}
                                            >
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="font-bold text-slate-200 flex items-center gap-2">
                                                        <PlatformIcon platform={item.name} className="w-3.5 h-3.5" />
                                                        {item.name}
                                                    </span>
                                                    <span className="font-mono font-black text-slate-100">
                                                        {item.value} <span className="text-[10px] text-slate-400 font-normal">({item.percentage}%)</span>
                                                    </span>
                                                </div>
                                                <div className="h-1.5 w-full bg-[#0B0F19] rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full transition-all duration-500"
                                                        style={{
                                                            width: `${Math.max(5, parseFloat(item.percentage))}%`,
                                                            backgroundColor: color
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* ── Threat Vector Categories ── */}
                        {stats.categories && stats.categories.length > 0 && (
                            <div className="bg-[#0F172A]/90 backdrop-blur-xl rounded-3xl border border-white/[0.08] p-6 shadow-xl space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xs font-black text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                        <Layers className="w-4 h-4 text-purple-400" /> Threat Vector Vectors
                                    </h3>
                                    <span className="text-[10px] font-mono text-slate-400 bg-[#1E293B] px-2 py-0.5 rounded border border-white/[0.06]">
                                        {stats.categories.length} Categories
                                    </span>
                                </div>

                                <div className="space-y-2.5">
                                    {stats.categories.slice(0, 5).map((cat, idx) => (
                                        <div
                                            key={cat.name}
                                            onClick={() => setSelectedCategory(selectedCategory === cat.name ? "all" : cat.name)}
                                            className={`p-2.5 rounded-xl border text-xs cursor-pointer transition-all ${
                                                selectedCategory === cat.name
                                                    ? "bg-purple-600/20 border-purple-500"
                                                    : "bg-[#1E293B]/30 border-white/[0.04] hover:bg-[#1E293B]/60"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between font-medium mb-1">
                                                <span className="text-slate-200 truncate">{cat.name}</span>
                                                <span className="font-mono font-bold text-purple-400">{cat.value}</span>
                                            </div>
                                            <div className="h-1 w-full bg-[#0B0F19] rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                                                    style={{ width: `${Math.max(6, parseFloat(cat.percentage))}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Tactical Advisory & Response Center ── */}
                        <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B]/80 to-[#1e1b4b]/50 backdrop-blur-xl rounded-3xl border border-blue-500/20 p-6 shadow-2xl space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-black text-slate-100 uppercase tracking-widest flex items-center gap-2">
                                    <ShieldAlert className="w-4 h-4 text-blue-400" /> Tactical Advisory Hub
                                </h3>
                                <span className="text-[9px] font-black uppercase tracking-widest bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded border border-blue-500/30">
                                    SOC Actions
                                </span>
                            </div>

                            <div className="space-y-3">
                                {analysis.recommendations && analysis.recommendations.length > 0 ? (
                                    analysis.recommendations.map((rec, i) => {
                                        return (
                                            <div
                                                key={i}
                                                className="p-3.5 bg-[#0B0F19]/80 rounded-2xl border border-white/[0.06] hover:border-blue-500/40 transition-all space-y-2 group"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400">
                                                            <Lock className="w-3.5 h-3.5" />
                                                        </div>
                                                        <h4 className="text-xs font-black text-slate-100 uppercase tracking-tight">
                                                            {rec.title}
                                                        </h4>
                                                    </div>
                                                    <span className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                                                        {rec.severity || "HIGH"}
                                                    </span>
                                                </div>

                                                <p className="text-[11px] text-slate-300 leading-relaxed font-medium">
                                                    {rec.desc}
                                                </p>

                                                <div className="flex items-center justify-between pt-1 border-t border-white/[0.04]">
                                                    <span className="text-[10px] text-slate-400 font-mono">
                                                        Target: <strong className="text-slate-200">{rec.targetedAsset || "Edge Gateway"}</strong>
                                                    </span>
                                                    <button
                                                        onClick={() => openMitigateDialog(rec.title, rec.targetedAsset)}
                                                        className="px-2.5 py-1 bg-blue-600/30 hover:bg-blue-600 text-blue-200 hover:text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1"
                                                    >
                                                        <Play className="w-2.5 h-2.5" /> Execute
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-xs text-slate-500 font-mono text-center py-4">No active tactical recommendations</p>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* ── TACTICAL MITIGATION CONFIRMATION MODAL ── */}
            {mitigationModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <div className="bg-[#0F172A] border border-blue-500/30 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4">
                        <div className="flex items-center justify-between border-b border-white/[0.08] pb-3">
                            <div className="flex items-center gap-2">
                                <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                    <Shield className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-black text-slate-100 uppercase tracking-widest">
                                        Deploy Tactical Mitigation
                                    </h3>
                                    <p className="text-[10px] text-slate-400 font-mono">SOC Countermeasure Dispatch</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setMitigationModal(prev => ({ ...prev, isOpen: false }))}
                                className="text-slate-400 hover:text-slate-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {mitigationModal.successMsg ? (
                            <div className="py-5 text-center space-y-4">
                                <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto text-emerald-400">
                                    <CheckCircle2 className="w-7 h-7" />
                                </div>
                                <div className="space-y-1">
                                    <h4 className="text-sm font-black text-slate-100 uppercase tracking-wider">Mitigation Dispatched &amp; Case Created</h4>
                                    <p className="text-xs text-slate-300 leading-relaxed max-w-sm mx-auto">{mitigationModal.successMsg}</p>
                                </div>

                                {mitigationModal.createdCaseId && (
                                    <div className="bg-[#1E293B]/80 border border-white/[0.08] p-3 rounded-2xl flex items-center justify-between font-mono text-xs text-left">
                                        <div>
                                            <span className="text-[10px] text-slate-400 uppercase font-bold block">Incident Case Reference</span>
                                            <span className="font-black text-blue-400">{mitigationModal.createdCaseId}</span>
                                        </div>
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                            IN_PROGRESS
                                        </span>
                                    </div>
                                )}

                                <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
                                    <button
                                        onClick={() => {
                                            setMitigationModal(prev => ({ ...prev, isOpen: false }));
                                            navigate("/cases");
                                        }}
                                        className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-blue-600/30 transition-all"
                                    >
                                        <Layers className="w-3.5 h-3.5" /> Open Cases Board
                                    </button>
                                    <button
                                        onClick={() => {
                                            setMitigationModal(prev => ({ ...prev, isOpen: false }));
                                            navigate("/incidents");
                                        }}
                                        className="w-full py-2.5 px-4 bg-[#1E293B] hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-black uppercase tracking-wider border border-white/[0.08] transition-all"
                                    >
                                        View in Incidents
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="bg-[#1E293B]/60 p-3.5 rounded-2xl border border-white/[0.06] space-y-1">
                                    <span className="text-[9px] font-black uppercase text-blue-400 tracking-widest">Action To Execute</span>
                                    <p className="text-sm font-bold text-slate-100">{mitigationModal.actionTitle}</p>
                                    <p className="text-xs text-slate-400 font-mono mt-1">Target Surface: <strong className="text-slate-200">{mitigationModal.targetedAsset}</strong></p>
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                        Analyst Dispatch Notes (Optional)
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Add operational context, ticket references, or justification..."
                                        className="w-full bg-[#1E293B]/80 border border-white/[0.08] rounded-xl p-3 text-xs text-slate-200 placeholder:text-slate-500 outline-none focus:border-blue-500/50 resize-none font-medium"
                                        value={mitigationModal.notes}
                                        onChange={(e) => setMitigationModal(prev => ({ ...prev, notes: e.target.value }))}
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-2">
                                    <button
                                        onClick={() => setMitigationModal(prev => ({ ...prev, isOpen: false }))}
                                        className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-slate-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleExecuteMitigation}
                                        disabled={mitigationModal.submitting}
                                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {mitigationModal.submitting ? (
                                            <>
                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                Executing...
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-3.5 h-3.5" /> Confirm &amp; Dispatch
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
